/*
  Shared behaviour, loaded once and deferred.

  Only components used by more than one section live here. Behaviour specific to
  a single section belongs in that section's {% javascript %} tag so it is
  bundled and shipped only with that section.
*/

(function () {
  "use strict";

  const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /**
   * Registers a custom element only once. The theme editor re-renders sections
   * without a full page load, so definitions can otherwise be attempted twice.
   */
  function define(name, constructor) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  }

  /**
   * A group of <details> elements where opening one closes the others.
   * Used by the FAQ section and the mobile plan cards.
   *
   * <accordion-group> or <accordion-group allow-multiple>
   */
  class AccordionGroup extends HTMLElement {
    connectedCallback() {
      this.allowMultiple = this.hasAttribute("allow-multiple");
      this.addEventListener("toggle", this.onToggle, true);
    }

    disconnectedCallback() {
      this.removeEventListener("toggle", this.onToggle, true);
    }

    onToggle = (event) => {
      const opened = event.target;
      if (this.allowMultiple || !opened.open) return;

      this.querySelectorAll("details[open]").forEach((details) => {
        if (details !== opened) details.open = false;
      });
    };
  }

  define("accordion-group", AccordionGroup);

  /**
   * Play/pause wrapper for a single <video>. The poster image stays visible
   * until playback starts, so the video itself can use preload="none" and cost
   * nothing on initial load.
   *
   * <video-player>
   *   <video ...></video>
   *   <button data-video-toggle>...</button>
   * </video-player>
   */
  class VideoPlayer extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector("video");
      this.toggle = this.querySelector("[data-video-toggle]");
      if (!this.video || !this.toggle) return;

      this.toggle.addEventListener("click", this.onToggleClick);
      this.video.addEventListener("play", this.onPlay);
      this.video.addEventListener("pause", this.onPause);
      this.video.addEventListener("ended", this.onPause);
    }

    disconnectedCallback() {
      if (!this.video) return;
      this.toggle.removeEventListener("click", this.onToggleClick);
      this.video.removeEventListener("play", this.onPlay);
      this.video.removeEventListener("pause", this.onPause);
      this.video.removeEventListener("ended", this.onPause);
    }

    onToggleClick = () => {
      if (this.video.paused) {
        // Only one video plays at a time across the whole page.
        document.querySelectorAll("video-player").forEach((player) => {
          if (player !== this) player.pause();
        });
        this.video.play();
      } else {
        this.video.pause();
      }
    };

    onPlay = () => {
      this.setAttribute("playing", "");
      this.toggle.setAttribute("aria-pressed", "true");
      this.toggle.setAttribute("aria-label", this.dataset.pauseLabel || "Pause video");
    };

    onPause = () => {
      this.removeAttribute("playing");
      this.toggle.setAttribute("aria-pressed", "false");
      this.toggle.setAttribute("aria-label", this.dataset.playLabel || "Play video");
    };

    pause() {
      if (this.video && !this.video.paused) this.video.pause();
    }
  }

  define("video-player", VideoPlayer);

  /**
   * Duplicates its own children once so a CSS marquee can translate -50% and
   * loop seamlessly. Doing this in JS rather than Liquid keeps the duplicated
   * copy out of the accessibility tree and out of the DOM when motion is
   * reduced.
   *
   * <marquee-row class="marquee__track">…items…</marquee-row>
   */
  class MarqueeRow extends HTMLElement {
    connectedCallback() {
      if (prefersReducedMotion() || this.dataset.duplicated === "true") return;

      const clone = this.cloneNode(true);
      clone.querySelectorAll("a, button, [tabindex]").forEach((el) => el.setAttribute("tabindex", "-1"));
      Array.from(clone.children).forEach((child) => {
        child.setAttribute("aria-hidden", "true");
        this.appendChild(child);
      });
      this.dataset.duplicated = "true";
    }
  }

  define("marquee-row", MarqueeRow);

  /**
   * Dismissible banner backed by localStorage, used by the region notice.
   *
   * <dismissible-banner storage-key="region-notice">
   *   <button data-dismiss>…</button>
   * </dismissible-banner>
   */
  class DismissibleBanner extends HTMLElement {
    connectedCallback() {
      this.key = `tws:dismissed:${this.getAttribute("storage-key") || "banner"}`;

      // Always visible in the theme editor so merchants can style it.
      if (!window.Shopify?.designMode && this.isDismissed()) {
        this.hidden = true;
        return;
      }

      this.querySelector("[data-dismiss]")?.addEventListener("click", () => {
        this.hidden = true;
        try {
          localStorage.setItem(this.key, "1");
        } catch (error) {
          // Private browsing blocks writes; the banner simply reappears later.
        }
      });
    }

    isDismissed() {
      try {
        return localStorage.getItem(this.key) === "1";
      } catch (error) {
        return false;
      }
    }
  }

  define("dismissible-banner", DismissibleBanner);

  /*
    Add-to-cart loader — native product forms (main-product, plan-card).

    Marks the submit button as busy so CSS can swap the label for a spinner,
    without setting `disabled` (that would drop `name="add"` from the POST
    payload and Shopify would reject the request). `pageshow` fires whenever
    the page is restored from the bfcache after Back from Checkout, so the
    button never comes back stuck in its loading state.
  */
  document.addEventListener("submit", (event) => {
    const submitter = event.submitter;
    if (!(submitter instanceof HTMLButtonElement)) return;
    if (submitter.name !== "add") return;
    submitter.setAttribute("aria-busy", "true");
  });

  window.addEventListener("pageshow", () => {
    document.querySelectorAll('button[name="add"][aria-busy="true"]').forEach((btn) => {
      btn.removeAttribute("aria-busy");
    });
  });

  window.theme = { prefersReducedMotion, define };
})();
